// MerchantParcelForm.jsx
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, MenuItem, Select, Chip, Avatar, IconButton,
    Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent,
    DialogActions, InputAdornment, CircularProgress, Collapse, LinearProgress
} from '@mui/material';
import Grid2 from '@mui/material/Grid2'; // or '@mui/material/Unstable_Grid2' - match your project
import PhoneInput, { isValidPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthWestIcon from '@mui/icons-material/SouthWest';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import dumyBike from '../../assets/images/vehicleTypeBike.svg';

const BLUE = '#0065FF';
const BORDER = '#E6F0FF';
const MUTED = '#6B7280';

/* ------------------------------------------------------------------ */
/* Data shapes                                                         */
/* ------------------------------------------------------------------ */
// mode: 'phone' (entering phone) | 'found' (fetched user) | 'manual' (typed by hand)
export const emptyContact = { phone: '', mode: 'phone', userId: null, name: '', flat: '' };
export const emptyParcel = {
    parcel_type_name: '', weight_unit: 1, weight: '', size_unit: 1, length: '', height: '',instructions: '',
};

// Adjust labels to whatever your backend enum means
const WEIGHT_UNITS = [
    { value: 1, label: 'kg' },   // Kilogram
    { value: 2, label: 'g' },    // Gram
    { value: 3, label: 'mg' },   // Milligram
    { value: 4, label: 'µg' },   // Microgram
    { value: 5, label: 't' },    // Tonne (metric)
    { value: 6, label: 'lb' },   // Pound
    { value: 7, label: 'oz' },   // Ounce
    { value: 8, label: 'ton' },  // Short ton (US)
];
const SIZE_UNITS = [
    { value: 1, label: 'm' },    // Metre
    { value: 2, label: 'cm' },   // Centimetre
    { value: 3, label: 'mm' },   // Millimetre
    { value: 4, label: 'µm' },   // Micrometre
    { value: 5, label: 'ft' },   // Foot
    { value: 6, label: 'in' },   // Inch
];

// Convert form values to the payload your API expects
export const toParcelPayload = (p = emptyParcel) => ({
    parcel_type_name: p.parcel_type_name || '',
    weight_unit: p.weight_unit ?? 1,
    weight: p.weight === '' ? 0 : Number(p.weight),
    size_unit: p.size_unit ?? 1,
    length: p.length === '' ? null : Number(p.length),
    height: p.height === '' ? null : Number(p.height),
    instructions: (p.instructions || '').trim(),
});

const contactComplete = (c) =>
    !!c &&
    !!c.name?.trim() &&
    !!c.phone &&
    isValidPhoneNumber(c.phone)
    && !!c.flat?.trim();
const parcelComplete = (p) => !!p && p.parcel_type_name?.trim() && Number(p.weight) > 0;

const parcelSummary = (p) => {
    if (!parcelComplete(p)) return '';
    const wu = WEIGHT_UNITS.find(u => u.value === p.weight_unit)?.label;
    const su = SIZE_UNITS.find(u => u.value === p.size_unit)?.label;
    const size = p.length && p.height ? ` · ${p.length}×${p.height} ${su}` : '';
    return `${p.parcel_type_name} · ${p.weight} ${wu}${size}`;
};

/* ------------------------------------------------------------------ */
/* Small UI helpers                                                    */
/* ------------------------------------------------------------------ */
const StatusChip = ({ done, doneLabel = 'Ready', pendingLabel = 'Incomplete' }) => (
    <Chip
        size="small"
        icon={done ? <CheckCircleIcon style={{ color: '#16A34A', fontSize: 16 }} /> : undefined}
        label={done ? doneLabel : pendingLabel}
        sx={{
            height: 22, fontSize: 12, fontWeight: 500,
            bgcolor: done ? '#F0FDF4' : '#FFF7ED',
            color: done ? '#16A34A' : '#C2410C',
        }}
    />
);

// Collapsible section. Works uncontrolled (default open) or controlled via expanded/onToggle.
const SectionCard = ({ icon, title, subtitle, done, children, accent = BLUE, expanded, onToggle }) => {
    const [inner, setInner] = useState(true);
    const isControlled = expanded !== undefined;
    const open = isControlled ? expanded : inner;

    // Auto-close when the section flips Incomplete -> Ready.
    // - "armed" is set whenever we see it incomplete, so a section the user manually re-opens stays open.
    // - No dependency array on purpose: any re-render (e.g. the user still typing) resets the 1.2s timer,
    //   so it only closes once the user has actually stopped editing.
    const armed = React.useRef(!done);
    React.useEffect(() => {
        if (!done) { armed.current = true; return; }
        if (!armed.current) return;
        const t = setTimeout(() => {
            armed.current = false;
            if (isControlled) onToggle?.(false); else setInner(false);
        }, 1200);
        return () => clearTimeout(t);
    });
    return (
        <Accordion
            expanded={open}
            onChange={(_, v) => (isControlled ? onToggle?.(v) : setInner(v))}
            disableGutters elevation={0}
            sx={{
                border: `1px solid ${BORDER}`, borderRadius: '12px !important', mb: 2,
                background: '#fff', '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: 2, '& .MuiAccordionSummary-content': { my: 1.25, alignItems: 'center' } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', pr: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: `${accent}14`, color: accent }}>{icon}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#111827' }}>{title}</Typography>
                        {subtitle && <Typography sx={{ fontSize: 12, color: MUTED }}>{subtitle}</Typography>}
                    </Box>
                    <StatusChip done={done} />
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                {children}
            </AccordionDetails>
        </Accordion>
    );
};

const FieldLabel = ({ children }) => (
    <Typography sx={{ fontSize: 13, color: '#4E545D', mb: 0.5 }}>{children}</Typography>
);

const inputSx = {
    '& .MuiInputBase-root': { height: 44, borderRadius: '8px', fontSize: 14 },
};

/* ------------------------------------------------------------------ */
/* Address input (keeps your Google Autocomplete ref working)          */
/* ------------------------------------------------------------------ */
const AddressInput = ({ inputRef, value, onChange, placeholder, marker, onFocus, onBlur }) => (
    <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{
            width: '100%', boxSizing: 'border-box', height: 44, borderRadius: 8,
            border: `1px solid ${BORDER}`, fontSize: 14, paddingLeft: 36,
            backgroundImage: marker ? `url(${marker})` : 'none',
            backgroundPosition: '10px center', backgroundRepeat: 'no-repeat', backgroundSize: '20px 20px',
        }}
    />
);

/* ------------------------------------------------------------------ */
/* ContactBlock: phone -> fetch -> found card | manual fields          */
/* ------------------------------------------------------------------ */
const ContactBlock = ({ value, onChange, onFetchUser, country }) => {
    const [loading, setLoading] = useState(false);
    const [hint, setHint] = useState('');
    const c = value || emptyContact;
    const phoneValid = !!c.phone && isValidPhoneNumber(c.phone);
    const phoneError = !!c.phone && !phoneValid;

    const fetchUser = async () => {
        if (!phoneValid) return;
        console.log('[ContactBlock] fetch clicked', { phone: c.phone, valid: phoneValid, hasHandler: !!onFetchUser });
        if (!c.phone) { setHint('Enter a phone number first'); return; }
        // if (!phoneValid) { setHint('Enter a valid phone number with country code'); return; }
        if (!onFetchUser) { setHint('Fetch handler not connected'); return; }
        setLoading(true); setHint('');
        try {
            const user = await onFetchUser(c.phone);
            console.log('[ContactBlock] fetch result', user);
            if (user) {
                onChange({ mode: 'found', userId: user.user_id, name: user.user_name || '', flat: user.flat_no || '' });
            } else {
                onChange({ mode: 'manual', userId: null });
                setHint('No user found - please enter details manually');
            }
        } catch (e) {
            console.error('[ContactBlock] fetch failed', e);
            onChange({ mode: 'manual', userId: null });
            setHint('Could not fetch user - enter details manually');
        } finally { setLoading(false); }
    };

    if (c.mode === 'found') {
        return (
            <Box sx={{ border: `1px solid ${BORDER}`, background: '#F9FBFF', borderRadius: '10px', p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: BLUE, width: 36, height: 36, fontSize: 15 }}>
                        {(c.name || '?')[0].toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, color: BLUE, fontSize: 15 }} noWrap>{c.name}</Typography>
                        <Typography sx={{ fontSize: 12, color: MUTED }}>{c.phone}</Typography>
                    </Box>
                    <Button
                        size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                        onClick={() => onChange({ ...emptyContact, phone: c.phone })}
                    >
                        Change
                    </Button>
                </Box>
                <Box sx={{ mt: 1.25 }}>
                    <TextField
                        fullWidth size="small" label="Flat / House no."
                        value={c.flat} onChange={(e) => onChange({ flat: e.target.value })}
                        sx={{ '& .MuiInputBase-root': { borderRadius: '8px', background: '#fff' } }}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box
                    sx={{
                        flex: 1, minWidth: 0,
                        ...(phoneError && {
                            '& .PhoneInput, & .PhoneInputInput': { borderColor: '#D14545 !important' },
                            '& .PhoneInputInput:focus': { outlineColor: '#D14545' },
                        }),
                    }}
                >
                    <PhoneInput
                        defaultCountry={country || 'IN'}
                        placeholder=" Enter phone number"
                        value={c.phone}
                        onChange={(v) => { setHint(''); onChange({ phone: v || '' }); }}
                        style={{ height: 44, fontSize: 15, alignItems: 'normal' }}
                        aria-invalid={phoneError}
                    />
                    {phoneError && (
                        <Typography sx={{ fontSize: 12, mt: 0.5, color: '#D14545' }}>
                            Enter a valid phone number
                        </Typography>
                    )}
                </Box>
                <Button
                    onClick={fetchUser} disabled={loading || phoneError}
                    sx={{
                        textTransform: 'none', borderRadius: '8px', height: 44, minWidth: 130,
                        color: BLUE, border: `1px solid ${BLUE}`,
                        '&.Mui-disabled': { borderColor: '#E5E7EB', color: '#9CA3AF' },
                    }}
                >
                    {loading ? <CircularProgress size={18} /> : 'Fetch Details'}
                </Button>
            </Box>

            {hint && (
                <Typography sx={{ fontSize: 12, mt: 0.75, color: c.mode === 'manual' ? '#C2410C' : '#D14545' }}>
                    {hint}
                </Typography>
            )}

            {/* {c.mode === 'phone' && (
                <Button
                    size="small" sx={{ textTransform: 'none', fontSize: 12, mt: 0.5, px: 0 }}
                    onClick={() => onChange({ mode: 'manual' })}
                >
                    Enter name & flat no. manually
                </Button>
            )} */}

            <Collapse in={c.mode === 'manual' && phoneValid} unmountOnExit>
                <Grid2 container spacing={1.5} sx={{ mt: 1 }}>
                    <Grid2 size={7}>
                        <TextField
                            value={c.name || ''}
                            onChange={(e) => onChange({ name: e.target.value })}
                            placeholder="Name" fullWidth size="small"
                        />
                    </Grid2>
                    <Grid2 size={5}>
                        <TextField
                            value={c.flat || ''}
                            onChange={(e) => onChange({ flat: e.target.value })}
                            placeholder="Flat / House no." fullWidth size="small"
                        />
                    </Grid2>
                </Grid2>
            </Collapse>
        </Box>
    );
};

// Adjust these keys to match what get_parcel_types returns
const typeLabel = (t) => t?.parcel_type_name ?? t?.name ?? '';
const typeKey = (t) => t?.parcel_type_id ?? t?.id ?? typeLabel(t);

/* ------------------------------------------------------------------ */
/* Parcel: inline summary + modal for the many fields                  */
/* ------------------------------------------------------------------ */
const ParcelDialog = ({ open, onClose, value, onSave, title, parcelTypes=[] }) => {
    const [d, setD] = useState(value || emptyParcel);
    useEffect(() => { if (open) setD({ ...emptyParcel, ...(value || {}) }); }, [open]); // eslint-disable-line
    const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));
    const numOnly = (v) => v.replace(/[^0-9.]/g, '');
    const canSave = d.parcel_type_name.trim() && Number(d.weight) > 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
                <Inventory2OutlinedIcon sx={{ color: BLUE, mr: 1 }} />
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 17 }}>Parcel details</Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>{title}</Typography>
                </Box>
                <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <FieldLabel>Parcel type</FieldLabel>
                    <Select
                        fullWidth displayEmpty
                        value={d.parcel_type_name || ''}
                        onChange={(e) => set('parcel_type_name', e.target.value)}
                        renderValue={(selected) =>
                            selected
                                ? selected
                                : <span style={{ color: '#9CA3AF' }}>Select parcel type</span>
                        }
                        sx={{ height: 44, borderRadius: '8px', fontSize: 14 }}
                    >
                        <MenuItem value="" disabled>Select parcel type</MenuItem>
                        {parcelTypes.length === 0 && (
                            <MenuItem value="" disabled>No parcel types available</MenuItem>
                        )}
                        {parcelTypes.map((t) => (
                            <MenuItem key={typeKey(t)} value={typeLabel(t)}>
                                {typeLabel(t)}
                            </MenuItem>
                        ))}
                    </Select>

                    <FieldLabel>
                        <Box component="span" sx={{ display: 'block', mt: 2 }}>Weight</Box>
                    </FieldLabel>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            fullWidth placeholder="0" value={d.weight}
                            onChange={(e) => set('weight', numOnly(e.target.value))}
                            inputProps={{ inputMode: 'decimal' }} sx={inputSx}
                        />
                        <Select
                            value={d.weight_unit} onChange={(e) => set('weight_unit', e.target.value)}
                            sx={{ minWidth: 100, height: 44, borderRadius: '8px' }}
                        >
                            {WEIGHT_UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>)}
                        </Select>
                    </Box>

                    <FieldLabel>
                        <Box component="span" sx={{ display: 'block', mt: 2 }}>
                            Size
                        </Box>
                    </FieldLabel>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            placeholder="Length" value={d.length ?? ''}
                            onChange={(e) => set('length', numOnly(e.target.value))}
                            inputProps={{ inputMode: 'decimal' }} sx={inputSx}
                        />
                        <TextField
                            placeholder="Height" value={d.height ?? ''}
                            onChange={(e) => set('height', numOnly(e.target.value))}
                            inputProps={{ inputMode: 'decimal' }} sx={inputSx}
                        />
                        <Select
                            value={d.size_unit} onChange={(e) => set('size_unit', e.target.value)}
                            sx={{ minWidth: 90, height: 44, borderRadius: '8px' }}
                        >
                            {SIZE_UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>)}
                        </Select>
                    </Box>

                    <FieldLabel>
                        <Box component="span" sx={{ display: 'block', mt: 2 }}>
                            Instructions <span style={{ color: MUTED }}>(optional)</span>
                        </Box>
                    </FieldLabel>
                    <TextField
                        fullWidth multiline minRows={2} maxRows={4}
                        placeholder="e.g. Fragile, call on arrival, leave at the security desk"
                        value={d.instructions || ''}
                        onChange={(e) => set('instructions', e.target.value.slice(0, 200))}
                        InputProps={{
                            sx: { borderRadius: '8px', fontSize: 14 },
                            endAdornment: (
                                <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 0.5 }}>
                                    <Typography sx={{ fontSize: 11, color: MUTED }}>{(d.instructions || '').length}/200</Typography>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#4E545D' }}>Cancel</Button>
                <Button
                    variant="contained" disabled={!canSave} disableElevation
                    onClick={() => { onSave(d); onClose(); }}
                    sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: BLUE, px: 3 }}
                >
                    Save parcel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ParcelRow = ({ value, onSave, title, parcelTypes }) => {
    const [open, setOpen] = useState(false);
    const done = parcelComplete(value);
    return (
        <>
            <Box
                onClick={() => setOpen(true)}
                sx={{
                    mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                    border: `1px ${done ? 'solid' : 'dashed'} ${done ? BORDER : BLUE}`,
                    background: done ? '#F9FBFF' : '#fff', borderRadius: '10px', px: 1.5, py: 1,
                    '&:hover': { background: '#F0F7FF' },
                }}
            >
                <Inventory2OutlinedIcon sx={{ color: BLUE, fontSize: 20 }} />
                <Typography sx={{ flex: 1, fontSize: 14, color: done ? '#111827' : BLUE }} noWrap>
                    {done ? parcelSummary(value) : 'Add parcel details'}
                </Typography>
                {done ? <EditOutlinedIcon sx={{ fontSize: 16, color: MUTED }} /> : <AddIcon sx={{ fontSize: 18, color: BLUE }} />}
            </Box>
            <ParcelDialog open={open} onClose={() => setOpen(false)} value={value} onSave={onSave} title={title} parcelTypes={parcelTypes} />
        </>
    );
};

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */
const MerchantParcelForm = ({
    // existing state/handlers from your page
    pickupLocation, setPickupLocation, dropLocation, setDropLocation,
    stops, setStops, handleAddStop, handleRemoveStop, handleStopLocationChange,
    handleStopFocus, inputRef, inputDropRef, stopRefs,
    pickupMarker, dropMarker,
    remove_add_stops, country_code,
    vehicleId, vehicleDetails = [], handleVehicleChange, cityFilter,
    // new
    fetchUserByPhone,            // async (phone) => ({user_id, user_name, flat_no} | null)
    onChange,                    // (payload) => void   (optional, sync data up to parent)
    onCreate, creating = false, parcelTypes = [], onFindVehicles, finding = false, routeReady = false, onInvalidate
}) => {
    const [sender, setSender] = useState(emptyContact);
    // const [senderNote, setSenderNote] = useState('');
    const [recipient, setRecipient] = useState(emptyContact);
    const [recipientParcel, setRecipientParcel] = useState(emptyParcel);
    const [expandedStop, setExpandedStop] = useState(null);
    const [stopsOpen, setStopsOpen] = useState(true);

    // Per-stop auto-close (same rules as SectionCard)
    const stopArmed = React.useRef({});
    const stopsSig = JSON.stringify(stops.map(s => [s.location, s.contact, s.parcel]));

    // When stops are added/removed, indexes shift: reset arming and re-open the Stops section
    useEffect(() => {
        stopArmed.current = {};
        setStopsOpen(true);
    }, [stops.length]);

    useEffect(() => {
        const i = expandedStop;
        if (i === null || !stops[i]) return;
        const s = stops[i];
        const ready = !!s.location && contactComplete(s.contact) && parcelComplete(s.parcel);
        if (!ready) { stopArmed.current[i] = true; return; }
        if (!stopArmed.current[i]) return;
        const t = setTimeout(() => {
            stopArmed.current[i] = false;
            setExpandedStop(null);
        }, 1200);
        return () => clearTimeout(t);
    }, [stopsSig, expandedStop]); // eslint-disable-line

    const patch = (setter) => (p) => setter(prev => ({ ...prev, ...p }));

    const stopsDone = stops.every(s => !!s.address && contactComplete(s.contact) && parcelComplete(s.parcel));
    const steps = [
        { label: 'Sender', done: !!pickupLocation && contactComplete(sender) },
        ...(stops.length ? [{ label: 'Stops', done: stopsDone }] : []),
        { label: 'Recipient', done: !!dropLocation && contactComplete(recipient) && parcelComplete(recipientParcel) },
        { label: 'Vehicle', done: !!vehicleId },
    ];
    const doneCount = steps.filter(s => s.done).length;
    // const canCreate = doneCount === steps.length;

    const buildForm = () => ({
        sender,
        recipient: { ...recipient, parcel: toParcelPayload(recipientParcel) },
        stops: stops.map(s => ({ ...s, parcel: toParcelPayload(s.parcel) })),
    });
    
    const detailsReady = steps.filter(s => s.label !== 'Vehicle').every(s => s.done);
    const hasVehicles = vehicleDetails.length > 0;
    const canFind = detailsReady && routeReady && !finding;
    const canCreate = detailsReady && !!vehicleId;
    
    const handleFind = () => onFindVehicles?.(buildForm());
    // const handleCreate = () => onCreate?.(buildForm());
    
    // Parcel edits change the fare/eligible vehicles, so force a re-find
    // const parcelSig = JSON.stringify([recipientParcel, stops.map(s => s.parcel)]);
    const fareFields = ({ instructions, ...rest } = {}) => rest;
    const parcelSig = JSON.stringify([fareFields(recipientParcel), stops.map(s => fareFields(s.parcel))]);

    const firstRun = React.useRef(true);
    useEffect(() => {
        if (firstRun.current) { firstRun.current = false; return; }
        onInvalidate?.();
    }, [parcelSig]);

    const handleCreate = () => onCreate?.({
        sender,
        recipient: { ...recipient, parcel: toParcelPayload(recipientParcel) },
        stops: stops.map(s => ({ ...s, parcel: toParcelPayload(s.parcel) })),
    });

    // Per-stop extras live on the stop object itself, so removing a stop never misaligns data
    const updateStop = (index, p) =>
        setStops(prev => prev.map((s, i) => (i === index ? { ...s, ...p } : s)));
    const patchStopContact = (index) => (p) =>
        updateStop(index, { contact: { ...emptyContact, ...(stops[index]?.contact || {}), ...p } });

    // Push everything up to the parent in API-ready shape
    useEffect(() => {
        onChange?.({
            sender,
            recipient: { ...recipient, parcel: toParcelPayload(recipientParcel) },
            stops: stops.map(s => ({ ...s, contact: s.contact, parcel: toParcelPayload(s.parcel) })),
        });
    }, [sender, recipient, recipientParcel, stops]); // eslint-disable-line

    const addStop = () => {
        handleAddStop?.();
        setExpandedStop(stops.length); // auto-open the new stop
    };

    return (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 210px)', minHeight: 460 }}>
            <Box sx={{ mb: 0.2 }}>
                {/* <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography sx={{ color: '#111827', fontWeight: 600, fontSize: 20 }}>New Parcel Order</Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>{doneCount}/{steps.length} sections ready</Typography>
                </Box> */}
                <LinearProgress
                    variant="determinate" value={(doneCount / steps.length) * 100}
                    sx={{
                        height: 6, borderRadius: 3, bgcolor: '#EEF2F7',
                        '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: canCreate ? '#16A34A' : BLUE },
                    }}
                />
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, pb: 1 }}>
                
            {/* ---------------- SENDER ---------------- */}
            <SectionCard
                icon={<NorthEastIcon fontSize="small" />}
                title="Sender" subtitle="Pickup address & contact"
                done={!!pickupLocation && contactComplete(sender)}
            >
                <FieldLabel>Pickup address</FieldLabel>
                <AddressInput
                    inputRef={inputRef} value={pickupLocation} onChange={setPickupLocation}
                    placeholder="Search pickup address" marker={pickupMarker}
                />
                <Box sx={{ mt: 1.5 }}>
                    <FieldLabel>Sender contact</FieldLabel>
                    <ContactBlock
                        value={sender} onChange={patch(setSender)}
                        onFetchUser={fetchUserByPhone} country={country_code}
                    />
                </Box>
                {/* <Box sx={{ mt: 1.5 }}>
                    <FieldLabel>
                        Instructions for pickup <span style={{ color: MUTED }}>(optional)</span>
                    </FieldLabel>
                    <TextField
                        fullWidth multiline minRows={2} maxRows={4}
                        placeholder="e.g. Call on arrival, parcel is at the security desk"
                        value={senderNote}
                        onChange={(e) => setSenderNote(e.target.value.slice(0, 200))}
                        InputProps={{
                            sx: { borderRadius: '8px', fontSize: 14 },
                            endAdornment: (
                                <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 0.5 }}>
                                    <Typography sx={{ fontSize: 11, color: MUTED }}>{senderNote.length}/200</Typography>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box> */}
            </SectionCard>

            {/* ---------------- STOPS ---------------- */}
            {stops.length > 0 && (
                <SectionCard
                    icon={<AltRouteIcon fontSize="small" />}
                    title={`Stops (${stops.length})`} subtitle="Intermediate pickup / drop points"
                    accent="#F59E0B"
                    done={stops.every(s => !!s.address && contactComplete(s.contact) && parcelComplete(s.parcel))}
                    expanded={stopsOpen} onToggle={setStopsOpen}
                >
                    {stops.map((stop, index) => {
                        const done = !!stop.address && contactComplete(stop.contact) && parcelComplete(stop.parcel);
                        return (
                            <Accordion
                                key={stop.stop_id || index}
                                expanded={expandedStop === index}
                                onChange={(_, open) => setExpandedStop(open ? index : null)}
                                disableGutters elevation={0}
                                sx={{
                                    border: `1px solid ${BORDER}`, borderRadius: '12px !important',
                                    mb: 1, '&:before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} 
                                sx={{ px: 2, '& .MuiAccordionSummary-content': { minWidth: 0, my: 1.25, alignItems: 'center' } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', pr: 1, minWidth: 0 }}>
                                        <span style={{
                                            flexShrink: 0,
                                            minWidth: 24, height: 24, borderRadius: '50%', background: BLUE,
                                            color: '#fff', fontWeight: 500, textAlign: 'center', lineHeight: '24px', fontSize: 13,
                                        }}>
                                            {stop.stop_id || index + 1}
                                        </span>
                                        <Typography sx={{ flex: 1, minWidth: 0, fontSize: 14, color: stop.address ? '#111827' : MUTED }} noWrap>
                                            {stop.address || 'Add stop address'}
                                        </Typography>
                                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <StatusChip done={done} />
                                            <IconButton
                                                size="small"
                                                aria-label={`Remove stop ${stop.stop_id || index + 1}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedStop(null);
                                                    handleRemoveStop(index);
                                                }}
                                                onFocus={(e) => e.stopPropagation()}
                                                sx={{ color: '#9CA3AF', '&:hover': { color: '#D14545', background: '#FEF2F2' } }}
                                            >
                                                <CloseIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </AccordionSummary>

                                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                                    <FieldLabel>Stop address</FieldLabel>
                                    <AddressInput
                                        inputRef={(el) => { if (stopRefs?.current) stopRefs.current[index] = el; }}
                                        value={stop.address}
                                        onChange={(v) => handleStopLocationChange(index, v)}
                                        onFocus={() => handleStopFocus?.(index)}
                                        placeholder={`Search stop ${index + 1} address`}
                                    />
                                    <Box sx={{ mt: 1.5 }}>
                                        <FieldLabel>Contact at this stop</FieldLabel>
                                        <ContactBlock
                                            value={stop.contact} onChange={patchStopContact(index)}
                                            onFetchUser={fetchUserByPhone} country={country_code}
                                        />
                                    </Box>
                                    <ParcelRow
                                        value={stop.parcel} title={`Stop ${stop.stop_id || index + 1}`}
                                        onSave={(p) => updateStop(index, { parcel: p })}
                                        parcelTypes={parcelTypes}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </SectionCard>
            )}

            {/* Add stop sits between sender and recipient, outside the card so it's always reachable */}
            {remove_add_stops != 1 && (
                <Button
                    fullWidth startIcon={<AltRouteIcon />} onClick={addStop}
                    sx={{
                        textTransform: 'none', borderRadius: '10px', height: 44, mb: 2,
                        border: `1px dashed ${BLUE}`, color: BLUE, '&:hover': { background: '#F0F7FF' },
                    }}
                >
                    Add stop
                </Button>
            )}

            {/* ---------------- RECIPIENT ---------------- */}
            <SectionCard
                icon={<SouthWestIcon fontSize="small" />}
                title="Recipient" subtitle="Drop address, contact & parcel"
                accent="#16A34A"
                done={!!dropLocation && contactComplete(recipient) && parcelComplete(recipientParcel)}
            >
                <FieldLabel>Drop address</FieldLabel>
                <AddressInput
                    inputRef={inputDropRef} value={dropLocation} onChange={setDropLocation}
                    placeholder="Search drop address" marker={dropMarker}
                />
                <Box sx={{ mt: 1.5 }}>
                    <FieldLabel>Recipient contact</FieldLabel>
                    <ContactBlock
                        value={recipient} onChange={patch(setRecipient)}
                        onFetchUser={fetchUserByPhone} country={country_code}
                    />
                </Box>
                <ParcelRow value={recipientParcel} title="Recipient" onSave={setRecipientParcel}
                parcelTypes={parcelTypes} />
            </SectionCard>

            {/* ---------------- VEHICLE ---------------- */}
            {hasVehicles ? (
            <SectionCard
                icon={<LocalShippingOutlinedIcon fontSize="small" />}
                title="Vehicle" subtitle="Choose the vehicle type for this delivery"
                accent="#7C3AED"
                done={!!vehicleId}
            >
                <FieldLabel>Vehicle type</FieldLabel>
                <Select
                    fullWidth displayEmpty
                    value={vehicleId ?? ''}
                    onChange={(e) => handleVehicleChange?.(e.target.value)}
                    renderValue={(selected) => {
                        const v = vehicleDetails.find((x) => x.vehicle_id === selected);
                        if (!v) return <span style={{ color: '#9CA3AF' }}>Select vehicle</span>;
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                    src={v.vehicle_image || dumyBike}
                                    sx={{ width: 28, height: 28, border: '1px solid #E5E7EB' }}
                                />
                                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                    {v.vehicle_name}
                                </Typography>
                                <Box
                                    sx={{
                                        ml: 'auto', px: 1, py: '2px', borderRadius: '999px',
                                        backgroundColor: '#E6F0FF', color: '#0065ff',
                                        fontSize: '12px', fontWeight: 600,
                                    }}
                                >
                                    {v.estimated_fare.toFixed(2)}
                                </Box>
                            </Box>
                        );
                    }}
                    sx={{
                        height: 44, borderRadius: '8px',
                        backgroundColor: cityFilter ? '#F9F9F9' : '#FFFFFF',
                    }}
                >
                    <MenuItem value="" disabled>Select vehicle</MenuItem>
                    {vehicleDetails.map((vehicle) => (
                        <MenuItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                <Avatar
                                    src={vehicle.vehicle_image || dumyBike}
                                    sx={{ width: 36, height: 36, border: '1px solid #E5E7EB' }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#111827', lineHeight: 1.2 }}>
                                        {vehicle.vehicle_name}
                                    </Typography>
                                    {vehicle.vehicle_type && (
                                        <Typography sx={{ fontSize: '12px', color: '#6B7280', lineHeight: 1.2 }}>
                                            {vehicle.vehicle_type}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ ml: 'auto', textAlign: 'right', flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: '11px', color: '#9CA3AF', lineHeight: 1.1 }}>
                                        Estimated fare
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#0065ff', lineHeight: 1.3 }}>
                                        {vehicle.estimated_fare.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </SectionCard>
            ) : (
                <Typography sx={{ fontSize: 13, color: MUTED, textAlign: 'center', py: 1 }}>
                    Complete sender, stops and recipient, then tap Find Vehicles.
                </Typography>
            )}

            </Box>

            <Box sx={{ pt: 1.5, mt: 1, borderTop: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* {finding ? 'Finding vehicles…' : hasVehicles ? 'Refresh Vehicles' : 'Find Vehicles'} */}
                {!hasVehicles && (
                    <Button
                        fullWidth disableElevation onClick={handleFind} disabled={!canFind}
                        variant="contained"
                        startIcon={finding ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{ height: 46, borderRadius: '10px', textTransform: 'none', fontSize: 15, fontWeight: 600 }}
                    >
                        {finding ? 'Finding vehicles…' : 'Find Vehicles'}
                    </Button>
                )}

                {hasVehicles && (
                    <Button
                        fullWidth variant="contained" disableElevation
                        disabled={!canCreate || creating} onClick={handleCreate}
                        startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <LocalShippingOutlinedIcon />}
                        sx={{
                            height: 48, borderRadius: '10px', textTransform: 'none', fontSize: 16, fontWeight: 600,
                            '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                        }}
                    >
                        {creating ? 'Creating order…' : 'Create Order'}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default MerchantParcelForm;

/* ------------------------------------------------------------------ */
/* USAGE in your page                                                  */
/* ------------------------------------------------------------------ */
/*
{merchantIdFromLocal != 0 ? (
    <MerchantParcelForm
        pickupLocation={pickupLocation} setPickupLocation={setPickupLocation}
        dropLocation={dropLocation} setDropLocation={setDropLocation}
        stops={stops} setStops={setStops}
        handleAddStop={handleAddStop} handleRemoveStop={handleRemoveStop}
        handleStopLocationChange={handleStopLocationChange} handleStopFocus={handleStopFocus}
        inputRef={inputRef} inputDropRef={inputDropRef} stopRefs={stopRefs}
        pickupMarker={pickupMarker} dropMarker={dropMarker}
        remove_add_stops={remove_add_stops} country_code={country_code}
        fetchUserByPhone={async (phone) => {
            // call your existing search API here and map the response:
            // const res = await api(...); return res?.user ? { user_id: res.user.user_id, user_name: res.user.user_name, flat_no: res.user.flat_no } : null;
        }}
        onChange={(data) => setMerchantParcelData(data)}
    />
) : ( ...existing non-merchant UI... )}
*/